import * as React from 'react'

import { Redo2Icon, RotateCcwIcon, Undo2Icon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Kbd } from '~/client/src/shared/components/shadcn-ui/kbd'
import Tabs from '~/client/src/shared/components/Tabs/Tabs'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import OperationRulesStore from '~/client/src/shared/stores/domain/OperationRules.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import { IWorkflowTypeRuleChanges } from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import PermitBallInCourtStore from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import FormConfigurator from '../FormConfigurator/FormConfigurator'
import Breadcrumb from './components/Breadcrumb'
import FormWorkflowSteps from './components/FormWorkflowSteps'
import GeneralFormConfigurator from './components/GeneralFormConfigurator/GeneralFormConfigurator'
import UndoRedoAction from './components/UndoRedoAction'
import Workflow from './components/Workflow/Workflow'
import WorkflowQRCodesTab from './components/WorkflowQRCodesTab'
import DisabledResourceIdsProvider from './DisabledResourceIdsProvider'
import FormTypeConfiguratorStore from './FormTypeConfigurator.store'
import { FormTypeConfiguratorExitContext } from './FormTypeConfiguratorExitContext'
import { FormTypeEnabledResourcesContext } from './FormTypeEnabledResourcesContext'
import UnsavedChangesDialog from './UnsavedChangesDialog'

// localization: translated

interface IProps {
  formType: WorkflowType
  isUpdating: boolean
  isDraft?: boolean

  onBackClick(): void
  onTemplateUpdate(template: WorkflowType): void
  saveFormType(
    updatedFormType: WorkflowType,
    onError?: () => void,
    onSaved?: () => void,
    ruleChanges?: IWorkflowTypeRuleChanges,
  ): void

  permitBicStore: PermitBallInCourtStore

  state?: InitialState
  projectDateStore?: ProjectDateStore
  formCategoriesStore?: WorkflowCategoriesStore
  operationRulesStore?: OperationRulesStore
}

const Z_KEY_CODE = 'KeyZ'
const Y_KEY_CODE = 'KeyY'
const MAC = 'MAC'
const isMac = navigator.platform.toUpperCase().includes(MAC)
const UNDO_SHORTCUT = isMac ? '⌘Z' : 'Ctrl+Z'
const REDO_SHORTCUT = isMac ? '⇧⌘Z' : 'Ctrl+Y'

@inject(
  'state',
  'projectDateStore',
  'formCategoriesStore',
  'operationRulesStore',
)
@observer
export default class FormTypeConfigurator extends React.Component<IProps> {
  private readonly store: FormTypeConfiguratorStore

  public constructor(props: IProps) {
    super(props)

    this.store = new FormTypeConfiguratorStore(
      props.formType,
      props.formCategoriesStore,
      props.saveFormType,
      props.onTemplateUpdate,
      props.operationRulesStore,
    )
  }

  public componentDidMount() {
    document.addEventListener('keydown', this.onKeyDown)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const { formType } = this.props
    if (
      formType?.id !== prevProps.formType?.id &&
      formType?.type !== prevProps.formType?.type
    ) {
      this.store.showToastErrorIfNeed()
    }
    if (formType !== prevProps.formType) {
      if (this.store.keepsDraftOver(formType)) {
        this.store.mergeRefresh(formType)
      } else {
        this.store.init(formType)
      }
    }
  }

  public componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown)

    this.store.showToastErrorIfNeed()
  }

  public render() {
    const {
      isGeneralTabSelected,
      isStepsTabSelected,
      isFormsTabSelected,
      isQRCodesTabSelected,
      formConfiguratorTabs,
      activeFormTab,
      changeActiveFormTab,
    } = this.store

    return (
      <FormTypeConfiguratorExitContext.Provider
        value={this.handleBreadcrumbGoBack}
      >
        <FormTypeEnabledResourcesContext.Provider value={this.store}>
          <DisabledResourceIdsProvider>
            <div className="col full-height full-width relative">
              <UnsavedChangesDialog
                store={this.store}
                isDraft={this.props.isDraft}
              />
              {this.actionBar}
              <div className="forms-tabs-container row x-between y-center mx20 overflow-hidden no-select">
                <Tabs
                  tabObjects={formConfiguratorTabs}
                  activeTabId={activeFormTab}
                  onTabClick={changeActiveFormTab}
                  className="bb-light-grey full-height"
                  tabTextClassName="px20"
                  tabClassName="forms-tab-item text large center line-extra-large pointer"
                />
              </div>
              {isGeneralTabSelected && this.generalTabContent}
              {isStepsTabSelected && this.workflowTabContent}
              {isFormsTabSelected && this.configuratorTabContent}
              {isQRCodesTabSelected && this.qrCodesTabContent}
            </div>
          </DisabledResourceIdsProvider>
        </FormTypeEnabledResourcesContext.Provider>
      </FormTypeConfiguratorExitContext.Provider>
    )
  }

  private get actionBar(): JSX.Element {
    const { isUpdating } = this.props
    const {
      formType,
      isAllowedToSave,
      hasUndo,
      hasRedo,
      warningMessage,
      activeTabTitle,
      undoChangesToInitial,
      undoChanges,
      redoChanges,
      saveChanges,
    } = this.store

    return (
      <div className="tw:flex tw:items-center tw:py-3 tw:px-5 tw:overflow-hidden tw:select-none">
        <div className="tw:flex tw:flex-1 tw:items-center tw:overflow-hidden tw:mr-5">
          <Breadcrumb
            label={Localization.translator.workflows}
            onClick={this.handleBreadcrumbGoBack}
          />
          <Breadcrumb label={formType.name} />
          <Breadcrumb label={activeTabTitle} isLast />
        </div>

        <div
          className={classList({
            'tw:flex tw:items-center tw:gap-2 tw:relative tw:shrink-0': true,
            'inactive-element': isUpdating,
          })}
        >
          <UndoRedoAction
            icon={RotateCcwIcon}
            label={Localization.translator.restoreToLastSavedState}
            isEnabled={hasUndo}
            tooltip={Localization.translator.restoreToLastSavedState}
            onClick={undoChangesToInitial}
          />
          <UndoRedoAction
            icon={Undo2Icon}
            label={Localization.translator.undo}
            isEnabled={hasUndo}
            tooltip={this.getUndoRedoTooltip(true)}
            onClick={undoChanges}
          />
          <UndoRedoAction
            icon={Redo2Icon}
            label={Localization.translator.redo}
            isEnabled={hasRedo}
            tooltip={this.getUndoRedoTooltip()}
            onClick={redoChanges}
          />
          <Button
            disabled={
              (!hasUndo && !this.props.isDraft) ||
              !isAllowedToSave ||
              isUpdating
            }
            onClick={saveChanges}
          >
            {Localization.translator.save}
          </Button>
        </div>
        {warningMessage && (
          <div className="absolute full-width bg-red text large bold center white mt5 py10 z-index-100 bottom-0 left-0">
            <span>
              {Localization.translator.error}: {warningMessage}
            </span>
          </div>
        )}
      </div>
    )
  }

  private get generalTabContent(): JSX.Element {
    const { formType, changeGeneralProps } = this.store

    return (
      <div className="col full-height pt10 px16 overflow-auto">
        <GeneralFormConfigurator
          formType={formType}
          getMonthDayYearAndTimeToDisplay={
            this.props.projectDateStore.getMonthDayYearAndTimeToDisplay
          }
          onChange={changeGeneralProps}
        />
      </div>
    )
  }

  private get workflowTabContent(): JSX.Element {
    const {
      formType,
      workflowSteps,
      hasUndo,
      bicRulesDraft,
      changeWorkflowSettings,
      selectStepAndGoToForms,
    } = this.store

    return (
      <div className="col full-height pt10 px20 overflow-auto">
        <Workflow
          formTypeId={formType.type}
          workflowSteps={workflowSteps}
          inspectionOptions={formType.inspectionOptions}
          isAutoActivationEnabled={formType.isAutoActivationEnabled}
          isUpdating={this.props.isUpdating}
          isWorkflowChanged={hasUndo}
          bicRulesDraft={bicRulesDraft}
          permitBallInCourtStore={this.props.permitBicStore}
          onWorkflowSettingsChange={changeWorkflowSettings}
          onStepClick={selectStepAndGoToForms}
        />
      </div>
    )
  }

  private get configuratorTabContent(): JSX.Element {
    const {
      formType: { workflowSteps, type: formTypeType },
      selectedWorkflowStepId,
      selectedWorkflowStep,
      isInitialStepSelected,
      filterFieldsMap,
      availableFilterFieldsOptions,
      changePermitTypeWorkflowStep,
      changeFieldNotificationSteps,
      removeFieldFromNotifications,
      setSelectedWorkflowStepId,
    } = this.store

    return (
      <>
        <span className="px50 py10 text large bold">
          {Localization.translator.workflowConfDescr.selectStepMsg}
        </span>
        <div className="row overflow-auto">
          <div className="row x-center">
            <FormWorkflowSteps
              selectedStepId={selectedWorkflowStepId}
              workflowSteps={workflowSteps}
              onStepClick={setSelectedWorkflowStepId}
            />
          </div>
        </div>
        <div className="px20 col full-height overflow-auto">
          <FormConfigurator
            workflowStep={selectedWorkflowStep}
            isInitialFormStep={isInitialStepSelected}
            onWorkflowStepChange={changePermitTypeWorkflowStep}
            workflowType={formTypeType}
            filterFieldsMap={filterFieldsMap}
            availableFilterFieldsOptions={availableFilterFieldsOptions}
            allWorkflowSteps={workflowSteps}
            onNotificationFieldStepsChange={changeFieldNotificationSteps}
            onRemoveFieldFromNotifications={removeFieldFromNotifications}
          />
        </div>
      </>
    )
  }

  private get qrCodesTabContent(): JSX.Element {
    return (
      <div className="tw:flex tw:justify-center tw:p-8 tw:overflow-auto tw:flex-1">
        <WorkflowQRCodesTab
          formType={this.store.formType}
          projectCode={this.props.state.activeProject.code}
        />
      </div>
    )
  }

  private getUndoRedoTooltip(isUndo?: boolean): JSX.Element {
    return (
      <>
        {isUndo ? Localization.translator.undo : Localization.translator.redo}
        <Kbd>{isUndo ? UNDO_SHORTCUT : REDO_SHORTCUT}</Kbd>
      </>
    )
  }

  private onKeyDown = (event: KeyboardEvent) => {
    const isCtrlPressed = event.ctrlKey || (isMac && event.metaKey)
    const isShiftPressed = event.shiftKey

    if (isMac && isCtrlPressed) {
      event.preventDefault()
    }

    switch (event.code) {
      case Z_KEY_CODE:
        if (isShiftPressed && isCtrlPressed) {
          this.store.redoChanges()
        } else if (isCtrlPressed) {
          this.store.undoChanges()
        }
        break
      case Y_KEY_CODE:
        if (isCtrlPressed) {
          this.store.redoChanges()
        }
        break
    }
  }

  private handleBreadcrumbGoBack = () => {
    const { hasUndo, displayUnsavedChangesDialog } = this.store
    const { onBackClick, isDraft } = this.props
    if (hasUndo || isDraft) displayUnsavedChangesDialog(onBackClick)
    else onBackClick()
  }
}
